// ADR-049/ADR-056: funções derivadas puras
export { eventoIndicaPendencia, isEncerrada } from './computed/eventoIndicaPendencia.js';
export { parsePrazo } from './computed/parsePrazo.js';
// Sessão 2026-05-19: classificação per-user — fonte única entre label e card
export { classifyDemandForUser, encontrarDemandadoDoUsuario } from './computed/classifyDemandForUser.js';
export { posturaRascunho } from './computed/posturaRascunho.js';
/** Status terminais — demandas nesses status não recebem toques (ADR-038 §12) */
export const STATUS_TERMINAL = [
    'CONCLUIDA', 'CANCELADA', 'OBSOLETA', 'MESCLADA',
];
/**
 * Display name usado em headers `From:` de emails autônomos do Flux,
 * formatado como `"Flux" <email-real>`. O email real vem da config
 * (env `GOOGLE_ADMIN_EMAIL`) — Flux não tem conta Gmail própria pra
 * evitar custo de licença Workspace.
 *
 * Defesa contra loop (ADR-045 §7): emails autônomos carregam header
 * `X-Flux-Auto: true` — esse é o marcador robusto, não o sender.
 */
export const FLUX_DISPLAY_NAME = 'Flux';
// ─── Mapas de display (1 mapa por enum, ADR-048 §3) ─────────────────────
/** StatusInferido → display (todos os 11 valores, incluindo terminais
 *  específicos). Usado no card pra mostrar motivo de encerramento e nuance
 *  de inversão/retomada. */
export const STATUS_INFERIDO_LABEL = {
    PENDENTE: { emoji: '⚪', label: 'Pendente' },
    ENGAJADA: { emoji: '⚙️', label: 'Engajada' },
    RETOMADA: { emoji: '🔄', label: 'Retomada' },
    ENTREGUE: { emoji: '📌', label: 'Entregue' },
    CLARIFICACAO: { emoji: '❓', label: 'Clarificação' },
    IMPEDIMENTO: { emoji: '🚧', label: 'Impedimento' },
    RENEGOCIACAO: { emoji: '🤝', label: 'Renegociação' },
    CONCLUIDA: { emoji: '✅', label: 'Concluída' },
    CANCELADA: { emoji: '🚫', label: 'Cancelada' },
    OBSOLETA: { emoji: '🗑️', label: 'Obsoleta' },
    MESCLADA: { emoji: '🔀', label: 'Mesclada' },
    SUGERIDA: { emoji: '💡', label: 'Sugerida' },
    CRIADA: { emoji: '➕', label: 'Criada' },
    ACOMPANHANDO: { emoji: '👀', label: 'Acompanhando' },
    PAROU_ACOMPANHAR: { emoji: '🙈', label: 'Parou de Acompanhar' },
    ACEITA_PRAZO: { emoji: '🤝', label: 'Aceitou Prazo' },
    REJEITA_PRAZO: { emoji: '👎', label: 'Rejeitou Prazo' },
};
/** EstadoVisual → label Gmail (ADR-047). Cores hex reusam FLUX_LABELS atual.
 *  `priority`: **menor = mais urgente/grave** — convenção do repo (FLUX_LABELS).
 *  Foreground (Pra Mim/Compartilhada) vence background (Em Andamento) na agregação. */
export const ESTADO_VISUAL_LABEL = {
    PRA_MIM: { emoji: '👤', label: 'Pra Mim', bg: '#fce8b3', text: '#285bac', priority: 3 },
    COMPARTILHADA: { emoji: '👥', label: 'Compartilhada', bg: '#fef1d1', text: '#3c78d8', priority: 4 },
    EM_ANDAMENTO: { emoji: '⚙️', label: 'Em Andamento', bg: '#4a86e8', text: '#ffffff', priority: 5 },
};
/** Flag → label Gmail (ADR-047). Cores hex reusam FLUX_LABELS atual.
 *  `priority`: **menor = mais urgente/grave** — convenção do repo (FLUX_LABELS).
 *  Atrasada (1) > No Limite (2) > Inadiável (8) — só 1 vai pro Gmail por vez. */
export const FLAG_LABEL = {
    ATRASADA: { emoji: '🚨', label: 'Atrasada', bg: '#ac2b16', text: '#ffffff', priority: 1 },
    NO_LIMITE: { emoji: '🔥', label: 'No Limite', bg: '#efa093', text: '#822111', priority: 2 },
    INADIAVEL: { emoji: '⚓', label: 'Inadiável', bg: '#1a764d', text: '#ffffff', priority: 8 },
};
/** Normalize demandados from DB (may be string or array) */
export function normalizeDemandados(raw) {
    if (!raw)
        return [];
    if (typeof raw === 'string')
        return JSON.parse(raw);
    return raw;
}
/**
 * ADR-068 §2.2 (D2): alvo do toque de PROCURAÇÃO. Quando há demandado EXTERNO (ikey null + email)
 * — sem inbox FLUX nem toque próprio — a cobrança cavalga o DEMANDANTE interno, que cobra o
 * externo na mão. Retorna o ikey do demandante a cutucar, ou null quando NÃO se aplica:
 *  - não há demandado externo (todos internos têm toque normal); OU
 *  - não há demandante interno (demandante externo/ausente — ninguém p/ cobrar); OU
 *  - o demandante já é um demandado interno (já tem toque normal → dedup).
 * Puro/determinístico — testável sem DB.
 */
export function procuracaoToqueTarget(demandados, demandante) {
    const temExterno = demandados.some(d => d.ikey == null && !!d.email);
    if (!temExterno || demandante == null)
        return null;
    const internos = new Set(demandados.filter(d => d.ikey != null).map(d => d.ikey));
    return internos.has(demandante) ? null : demandante;
}
// ─── Labels ──────────────────────────────────────────────────────────────────
/** Labels ↻ do FLUX — 6 labels após ADR-047 (5 antigos eliminados):
 *  - 3 papéis (mutuamente exclusivos): PRA_MIM, COMPARTILHADA, EM_ANDAMENTO
 *  - 3 flags ortogonais (1 ativa por vez): ATRASADA, NO_LIMITE, INADIAVEL
 *
 *  Aplicação: até 2 labels por thread — 1 papel + 1 flag (ADR-047 §8).
 *  Eliminados: ACAO (redundante), ATRIBUIDA (vira background), ENCERRADA
 *  (silêncio = nada pendente), ACOMPANHANDO (acompanhador é flag interna).
 *
 *  Migração (ADR-047): labels antigos não tocados; labels novos provisionados
 *  lazy via DWD na primeira interação. */
export const FLUX_LABELS = {
    // Papéis (foreground/background)
    PRA_MIM: { name: '↻👤 Pra Mim', priority: 3, bg: '#fce8b3', text: '#285bac', isFlag: false },
    COMPARTILHADA: { name: '↻👥 Compartilhada', priority: 4, bg: '#fef1d1', text: '#3c78d8', isFlag: false },
    EM_ANDAMENTO: { name: '↻⚙️ Em Andamento', priority: 5, bg: '#4a86e8', text: '#ffffff', isFlag: false },
    // Flags (urgência/prazo)
    ATRASADA: { name: '↻🚨 Atrasada', priority: 1, bg: '#ac2b16', text: '#ffffff', isFlag: true },
    NO_LIMITE: { name: '↻🔥 No Limite', priority: 2, bg: '#efa093', text: '#822111', isFlag: true },
    INADIAVEL: { name: '↻⚓ Inadiável', priority: 8, bg: '#1a764d', text: '#ffffff', isFlag: true },
};
/**
 * Deriva o estado visual per-user a partir do estado de pendência da demanda +
 * papel do usuário (ADR-056). Retorna `null` quando o usuário não tem label
 * visível (RECOLHIDO/INVISIVEL — ausência é o sinal positivo, ADR-047 §1).
 *
 * `acaoPendenteDest` = o polo DESTINATARIO tem ação pendente? (último evento
 * DESTINATARIO com `acao_pendente=true`). Substitui o antigo `StatusLifecycle`:
 * `acaoPendenteDest=true` ≡ "bola com (ou incluindo) o demandado"; `false` ≡
 * "bola com o demandante". `encerrada` = status terminal (silêncio na inbox).
 *
 * Pura, testável sem efeito colateral.
 */
export function computeEstadoVisual(encerrada, papel, acaoPendenteDest, jaEngajou = false, 
/** Outro membro do grupo (que não o usuário) já engajou? Implementa a
 *  transitoriedade do Compartilhada (ADR-047 §3): quando alguém do grupo
 *  assume a bola, os outros saem do foreground. */
groupHasOtherRespondent = false) {
    // Encerrada (status terminal): silêncio na inbox (ADR-047 §9)
    if (encerrada)
        return null;
    switch (papel) {
        case 'NENHUM':
            // Sem papel → recolhido (sem label)
            return null;
        case 'ACIONADO_INDIVIDUAL':
            // Bola com o demandado → PRA_MIM (foreground). Sem pendência do demandado
            // (entregou/devolveu) → EM_ANDAMENTO (background, aguardo feedback, §6).
            return acaoPendenteDest ? 'PRA_MIM' : 'EM_ANDAMENTO';
        case 'ACIONADO_GRUPO':
            // Compartilhada é transitória (ADR-047 §3). Bola fora do demandado →
            // background; quem engajou → PRA_MIM; alguém do grupo já pegou → background;
            // senão → aberta pro grupo (COMPARTILHADA).
            if (!acaoPendenteDest)
                return 'EM_ANDAMENTO';
            if (jaEngajou)
                return 'PRA_MIM';
            if (groupHasOtherRespondent)
                return 'EM_ANDAMENTO';
            return 'COMPARTILHADA';
        case 'DEMANDANTE':
            // Bola comigo (demandado sem pendência → me devolveu) → PRA_MIM.
            // ADR-056 trade-off: durante RENEGOCIACAO o demandado segue pendente
            // (acaoPendenteDest=true), então o demandante fica EM_ANDAMENTO (antes,
            // COM_AMBOS o deixava PRA_MIM). Caso de borda aceito.
            return acaoPendenteDest ? 'EM_ANDAMENTO' : 'PRA_MIM';
        case 'ACOMPANHADOR':
            // Background enquanto há ação pendente; encerrada já tratado acima
            return 'EM_ANDAMENTO';
    }
}
/**
 * Deriva flags ativas (urgência + prazo) pra uma demanda. Pode retornar
 * múltiplas, mas só 1 vai pro Gmail por prioridade ATRASADA > NO_LIMITE >
 * INADIAVEL (ADR-047 §7).
 *
 * Pura, testável sem efeito colateral.
 */
export function computeFlagsAtivas(demanda, agora = new Date(), 
/** T0 (data_envio da mensagem que criou a demanda) — opcional. Quando
 *  fornecido, No Limite é calculado em cima de (T0, prazo) reais.
 *  Sem T0, cai num fallback assumindo janela = PRAZO_FALLBACK_DIAS_UTEIS
 *  dias corridos pra trás do prazo (best-effort, ADR-049). */
t0) {
    const flags = [];
    // ADR-049: consome `prazo TIMESTAMPTZ` materializado em vez de `prazo_combinado`
    // (string humana parseável só via parsePrazo). Cron e UI usam direto.
    if (demanda.prazo) {
        const prazoMs = Date.parse(demanda.prazo);
        if (!isNaN(prazoMs)) {
            const prazo = new Date(prazoMs);
            if (agora >= prazo) {
                flags.push('ATRASADA');
            }
            else {
                const janelaMs = t0
                    ? prazo.getTime() - t0.getTime()
                    : DEFAULTS.PRAZO_FALLBACK_DIAS_UTEIS * 24 * 60 * 60 * 1000;
                const decorridoMs = janelaMs - (prazo.getTime() - agora.getTime());
                const fracao = Math.max(0, Math.min(1, decorridoMs / janelaMs));
                const limite = demanda.inadiavel
                    ? DEFAULTS.NO_LIMITE_THRESHOLD_INADIAVEL
                    : DEFAULTS.NO_LIMITE_THRESHOLD;
                if (fracao >= limite)
                    flags.push('NO_LIMITE');
            }
        }
    }
    if (demanda.inadiavel)
        flags.push('INADIAVEL');
    return flags;
}
/** Escolhe a flag visível no Gmail (1 só, prioridade ADR-047 §7). */
export function pickFlagVisivel(flags) {
    if (flags.length === 0)
        return null;
    // priority menor = mais urgente
    let best = flags[0];
    for (const f of flags) {
        if (FLAG_LABEL[f].priority < FLAG_LABEL[best].priority)
            best = f;
    }
    return best;
}
// ─── Constantes ──────────────────────────────────────────────────────────────
/** Defaults do MVP (ADR-038) */
export const DEFAULTS = {
    /** Prazo fallback quando nenhuma fonte define */
    PRAZO_FALLBACK_DIAS_UTEIS: 10, // [ADR-064 Fase 3] default 10 du (~1 semana) — decisão do usuário (era 7)
    /** Max tokens por documento na truncagem */
    MAX_TOKENS_PER_DOC: 4000,
    /** Max tokens por skill no frontmatter (~100-130 estimado) */
    MAX_TOKENS_FRONTMATTER: 150,
    /** Watch Gmail expira em ~7 dias */
    WATCH_EXPIRATION_DAYS: 7,
    /** Progressão Fibonacci para toques em dias úteis (ADR-038 §6).
     *  Infinita: ao atingir o fim, repete o último intervalo. */
    TOQUE_FIBONACCI: [1, 1, 2, 3, 5, 8, 13, 21],
    /** No Limite: threshold % do prazo — normal vs inadiável (ADR-038 §4) */
    NO_LIMITE_THRESHOLD: 0.75,
    NO_LIMITE_THRESHOLD_INADIAVEL: 0.50,
    /** ADR-045: taxa de rejeição de sugestões acima da qual a sensibilidade
     *  da análise meta noturna é reduzida (evita ruído na fila do autor). */
    SKILL_SUGESTAO_REJEITADA_THRESHOLD: 0.15,
    /** ADR-046 §8: threshold de interseção pro match de demandas durante
     *  reprocess (UPDATE vs INSERT+OBSOLETA). Calibrável. */
    RECONCILE_OVERLAP_THRESHOLD: 0.80,
    /** Sessão 2026-05-19: fallback de reconciliação por interseção de
     *  demandados (ikeys) quando a âncora por posição falha (LLM
     *  não-determinística em posicao_inicio/fim). Aplicado só em
     *  compartilhadas. 0.50 = pelo menos metade dos ikeys coincidem. */
    RECONCILE_DEMANDADOS_THRESHOLD: 0.50,
    /** Sessão 2026-05-21: threshold para similaridade semântica Jaccard
     *  utilizada como fallback no reprocessamento para evitar duplicação. */
    RECONCILE_SEMANTIC_THRESHOLD: 0.50,
    /** ADR-044 §1: confidence mínimo pra demanda aparecer como regular (não
     *  fantasma). 0.80 inicial — calibrável conforme feedback humano. */
    CONFIANCA_DEMANDA_THRESHOLD: 0.80,
};
/** SLAs de prazo (ADR-049 §14 + ADR-038 §5). Defaults globais — override
 *  por grupo/skill é feature pós-MVP via config. */
export const SLA_CIENCIA_HORAS_UTEIS = 24;
export const SLA_ESCALACAO_HORAS_UTEIS = 48;
/**
 * Rollout gates — toques automáticos (ADR-038).
 * Controlados via env vars para rollout gradual sem redeploy.
 */
/** ikeys cujas demandas recebem agendamento automático de toques.
 *  [ADR-064] default = joseluis (23296814) — o tester. O firing/email continua bounded à
 *  FLUX_AUTO_EMAIL_ALLOWLIST pelo hard-scope do scheduler, então isto só decide quem CRIA
 *  toques, não quem recebe (a allowlist decide o recebimento). */
export const TOQUE_BETA_IKEYS = new Set((process.env.TOQUE_BETA_IKEYS || '23296814').split(',').filter(Boolean).map(Number));
// [ADR-064] A cobrança é comportamento PERMANENTE (sem flag de liga/desliga — não será
// desligada). O controle/segurança é a FLUX_AUTO_EMAIL_ALLOWLIST: ela bounda os destinatários
// de email E (via hard-scope do scheduler) quais toques processam. Suppressão é sempre
// ciência-aware; emails disparam no nível ≥2, sempre clampados à allowlist.
/** ADR-060 §6: bloqueio mãe↔filha de toques. Enquanto a mãe tem ≥1 filha FORMAL
 *  (aderida) não-terminal, os toques dela são suprimidos (ela está legitimamente
 *  aguardando a filha). Supressão é query-side em getToquesPendentes (não zera
 *  proximo_toque), então o unblock é PASSIVO: quando a última filha vira terminal,
 *  a mãe re-casa a query e dispara no próximo batch. Default: ON (config no código
 *  sobrevive reinstall; env=false desliga sem redeploy). */
export const MAE_FILHA_BLOQUEIO_ENABLED = (process.env.MAE_FILHA_BLOQUEIO_ENABLED ?? 'true') !== 'false';
/** Identidade visual e auth FLUX (ADR-010) */
export const BRANDING = {
    PRIMARY_COLOR: '#FFC107',
    ICON: 'published_with_changes',
    /** OAuth Client ID — público por design (GIS Sign In button + verifyIdToken audience) */
    GOOGLE_CLIENT_ID: '90782771049-la06ur3114o0535f52q162m1065lvkhr.apps.googleusercontent.com',
};
/** Classes do Engine para igroupuser */
export const ENGINE_CLASSES = {
    GRUPO: -1898187810,
    USUARIO: -1898187809,
    ATIVO: -1898143909,
    /** Raiz da árvore "Centros de Custos ou Resultados": as skills moram nas classes dela (ADR-070). */
    CENTROS_DE_CUSTO: -2007877000,
};
// ─── System Sender Detection ─────────────────────────────────────────────────
/**
 * Patterns that identify system/automated email senders.
 * These addresses have no human behind them — demands from these senders
 * need a promoted demandante (first To recipient).
 */
const SYSTEM_SENDER_PATTERNS = [
    /nao[_-]?responda/i,
    /no[_-]?reply/i,
    /automatizado/i,
    /mailer[_-]?daemon/i,
    /postmaster/i,
    /bounce/i,
    /^notifications?$/i, // keep anchored: too generic unanchored (e.g. "sales-notifications-team")
    /^alert[s]?$/i, // keep anchored: too generic unanchored
];
const SYSTEM_DOMAIN_PATTERNS = [
    /notifications?\./i,
    /^mailer\./i,
    /bounce/i,
];
/** Check if an email address belongs to an automated system (no human demandante). */
export function isSystemSender(email) {
    if (!email)
        return false;
    const [local, domain] = email.split('@');
    const matchesLocal = SYSTEM_SENDER_PATTERNS.some(p => p.test(local));
    const matchesDomain = domain ? SYSTEM_DOMAIN_PATTERNS.some(p => p.test(domain)) : false;
    return matchesLocal || matchesDomain;
}
/**
 * Detect automated/machine-generated emails via RFC headers + heuristic.
 *
 * Safe signals (do NOT catch Google Groups):
 *   1. Auto-Submitted ≠ "no" (RFC 3834 — canonical)
 *   2. X-Auto-Response-Suppress present (Microsoft convention)
 *   3. Precedence: bulk (newsletter, mass-mail)
 *   4. isSystemSender(from) — heuristic on local part
 *
 * Explicitly IGNORED:
 *   - Precedence: list — Google Groups uses this for human-sent emails
 *   - List-Id alone — delivery mechanism, not sender nature
 */
export function isAutomatedEmail(fromEmail, headers) {
    // 1. RFC 3834: Auto-Submitted is the canonical signal
    if (headers.autoSubmitted && headers.autoSubmitted.toLowerCase() !== 'no') {
        return true;
    }
    // 2. Microsoft: X-Auto-Response-Suppress
    if (headers.xAutoResponseSuppress) {
        return true;
    }
    // 3. Precedence: bulk (NOT list — list is used by Google Groups for human emails)
    if (headers.precedence && headers.precedence.toLowerCase() === 'bulk') {
        return true;
    }
    // 4. Heuristic: system sender patterns (noreply@, bounce@, etc.)
    if (isSystemSender(fromEmail)) {
        return true;
    }
    // 5. Google Groups rewrites From for DMARC — original sender preserved in X-Original-Sender.
    //    Catches services like Looker Studio (data-studio-noreply@google.com) routed via internal groups.
    if (headers.xOriginalSender && isSystemSender(headers.xOriginalSender)) {
        return true;
    }
    return false;
}
/** Assunto de resposta automática de ausência (férias, fora do escritório), nos idiomas que chegam. */
const ASSUNTO_AUSENCIA_RE = /^\s*(resposta autom[áa]tica|respuesta autom[áa]tica|automatic reply|auto[- ]?reply|out of office|ausente|fora do escrit[óo]rio)\b/i;
/**
 * Resposta automática de AUSÊNCIA de uma pessoa (férias, fora do escritório) — não a devolução
 * de entrega (mailer-daemon/postmaster), que é remetente de sistema e continua na conversa porque
 * a LLM tira dela o "endereço inválido". Sinal canônico: `Auto-Submitted: auto-replied` (RFC 3834,
 * o que o Gmail põe na resposta de férias). Fallback para quem não manda o cabeçalho e para as
 * mensagens gravadas antes de ele ser guardado: e-mail automatizado (ADR-039) com assunto de ausência.
 */
export function isRespostaDeAusencia(fromEmail, autoSubmitted, assunto, automated) {
    if (isSystemSender(fromEmail))
        return false;
    if (autoSubmitted && autoSubmitted.trim().toLowerCase() === 'auto-replied')
        return true;
    return automated && ASSUNTO_AUSENCIA_RE.test(assunto ?? '');
}
// ADR-049 §18 (convenção opcional): snooze por label do Gmail
export * from './snooze-labels.js';
export * from './skills.js';
//# sourceMappingURL=index.js.map